import ReactECharts from "echarts-for-react";
import { useMetricQuery } from "../hooks/useMetricQuery";
import { formatChartAxisLabel, formatChartTooltip } from "../utility/datetime.js";

const DEFAULT_GROUP = "analytics2";

// Canonical colors per sensor code, matching the physical hardware labeling.
// If a sensor code ends in a number > 1 (e.g. orange2) it gets a lighter variant
// of the base color so two same-type sensors are distinguishable on the same chart.
const SENSOR_BASE_COLORS = {
  orange1: "#f97316",
  orange2: "#fdba74",
  blue1:   "#3b82f6",
  blue2:   "#93c5fd",
  white1:  "#e5e7eb",
  white2:  "#f9fafb",
};

function sensorColor(sensorCode, fallback) {
  if (!sensorCode) return fallback;
  if (SENSOR_BASE_COLORS[sensorCode]) return SENSOR_BASE_COLORS[sensorCode];
  // For unknown codes ending in a digit, try the base name with "1"
  const base = sensorCode.replace(/\d+$/, "1");
  return SENSOR_BASE_COLORS[base] ?? fallback;
}

export default function MetricChart({ title, metric, hostname, unit, color, timeRange, sensorLabels = {}, marks = {}, chartGroup = DEFAULT_GROUP, extraSeries = [], photoSeries = null, onPhotoClick, darkMode = false, timeZone, sensorCodes = null }) {
  const tz = timeZone || Intl.DateTimeFormat().resolvedOptions().timeZone;
  const { start, end } = timeRange;
  const { data, isLoading, isError } = useMetricQuery({ metric, hostname, start, end });

  // sensorCodes: optional Set of sensor codes to show; null means every sensor on the device.
  const sensors = (data ?? []).filter((s) => !sensorCodes || sensorCodes.has(s.sensor));

  // Each sensor gets a color based on its sensor_code (orange1 → orange, blue1 → blue, etc.)
  // Falls back to the chart's base color prop for unknown codes
  const series = [
    ...sensors.map((s, i) => {
      const c = sensorColor(s.sensor, color);
      const name = sensorLabels[s.sensor] || s.sensor || metric;
      return {
        name,
        type: "line",
        smooth: true,
        symbol: "none",
        lineStyle: { color: c, width: 2 },
        itemStyle: { color: c },
        areaStyle: { color: c, opacity: 0.08 },
        data: s.points.map((p) => [p.t, p.v]),
        connectNulls: false,
        // Inject marks (markArea, markLine, markPoint) into the first series only
        ...(i === 0 ? marks : {}),
      };
    }),
    ...extraSeries,
    ...(photoSeries ? [photoSeries] : []),
  ];

  const axisColor = darkMode ? "#4b5563" : "#6b7280";
  const gridColor = darkMode ? "#4b5563" : "#e5e7eb";
  const textColor = darkMode ? "#9ca3af" : "#374151";

  const option = {
    animation: false,
    grid: { top: 36, right: 16, bottom: 36, left: 56, containLabel: false },
    legend: {
      top: 4,
      textStyle: { color: textColor, fontSize: 11 },
      icon: "circle",
      itemWidth: 8,
      itemHeight: 8,
    },
    tooltip: {
      trigger: "axis",
      axisPointer: { type: "cross", animation: false },
      formatter: (params) => {
        const ts = formatChartTooltip(params[0].axisValue, tz);
        const rows = params
          .map((p) => `${p.seriesName}: <b>${p.data[1].toFixed(2)} ${unit}</b>`)
          .join("<br/>");
        return `${ts}<br/>${rows}`;
      },
    },
    // dataZoom enables scroll-to-zoom and the drag slider at the bottom
    dataZoom: [
      {
        type: "inside",
        filterMode: "filter",
      },
      {
        type: "slider",
        height: 20,
        bottom: 4,
        borderColor: "transparent",
        fillerColor: "rgba(99,102,241,0.15)",
        handleStyle: { color: "#6366f1" },
        textStyle: { fontSize: 10 },
        labelFormatter: (val) => formatChartAxisLabel(val, tz, end - start),
      },
    ],
    xAxis: {
      type: "time",
      axisLabel: {
        formatter: (val) => formatChartAxisLabel(val, tz, end - start),
        hideOverlap: true,
        fontSize: 11,
        color: textColor,
      },
      axisLine: { lineStyle: { color: axisColor } },
      splitLine: { show: false },
    },
    yAxis: [
      {
        type: "value",
        name: unit,              // axis label on the left showing the unit
        nameLocation: "end",
        nameTextStyle: { color: textColor, fontSize: 11, padding: [0, 0, 0, -40] },
        axisLabel: { formatter: `{value}`, fontSize: 11, color: textColor },
        axisLine: { lineStyle: { color: axisColor } },
        splitLine: { lineStyle: { type: "dashed", color: gridColor } },
      },
      // Hidden [0,1] axis for pinning photo thumbnails to a constant top band,
      // independent of the metric's auto-scaled axis. Only used when photoSeries is set.
      {
        type: "value",
        min: 0,
        max: 1,
        show: false,
        axisPointer: { show: false },
      },
    ],
    series,
  };

  return (
    <div className={`${darkMode ? 'bg-gray-700' : 'bg-white border border-gray-200'} rounded-lg p-4 flex flex-col gap-2`}>
      <h3 className={`text-sm font-medium ${darkMode ? 'text-gray-300' : 'text-gray-700'}`}>{title}</h3>

      {isLoading && (
        <div className="flex items-center justify-center h-40 text-gray-500 text-sm">Loading…</div>
      )}
      {isError && (
        <div className="flex items-center justify-center h-40 text-red-400 text-sm">Failed to load data</div>
      )}
      {!isLoading && !isError && (
        <ReactECharts
          option={option}
          style={{ height: 200 }}
          opts={{ renderer: "canvas" }}
          notMerge
          onChartReady={(chart) => chart.group = chartGroup}
          onEvents={{
            click: (params) => {
              if (params.seriesName === "Photos" && params.data?.photoId != null) {
                onPhotoClick?.(params.data.photoId);
              }
            },
          }}
        />
      )}
    </div>
  );
}
